import React, { useRef, useEffect, useState } from "react";
import { Dropdown } from "react-bootstrap";
import classNames from "classnames";
import useRoveFocus from "../../../utilities/useRoveFocus";
import { useSearchContext } from "../SearchProvider";
import IStyledProps from "../../../utilities/IStyledProps";
import IEditableProps from "../../../utilities/IEditableProps";
import styled from 'styled-components';
import useLanguage from "../../../utilities/useLanguage";
import { getTranslation } from '../translations';
import { AutomationTestIdElementType, AutomationTestIdFeature, createDataTestId } from "../../../utilities/automation";

const ResultsWrapper = styled.div`
    .dropdown {
        opacity: 1;
    }

    .dropdown-item {
        white-space: normal;
        word-break: break-word;
    }

    .dropdown-item:hover, .dropdown-item:focus {
        background-color: var(--primary);
        color: #fff;
    }

    .results-title, .results-description {
        font-size: 0.88889rem;
    }

    .results-description:not(:empty):before {
        content: " - ";
    }

    .search-results-list {
        display: block;
        max-width: 100%;
        box-shadow: 0px 4px 15px 0px rgba(0, 0, 0, 0.10);
        transform: scaleY(0);
        opacity: 0 !important;
        visibility: hidden;
        transform-origin: top center;
        transition: all .25s;
    }

    .search-results-container.show .search-results-list {
        transform: scaleY(1);
        opacity: 1 !important;
        visibility: visible;
    }

    .search-results-container {
        margin-top: 9px;
    }

    .dropdown-menu {
        width: auto;
        cursor: pointer;

        &.show {
            opacity: 1 !important;
            pointer-events: all !important;
        }
    }

`;

/**
 * Displays a list of results
 */
export function ResultItem(props) {
    const { itemAs = "", title, href, focus, children, onItemClick } = props;
    const ref = useRef(null);

    useEffect(() => {
        if (focus) {
            // Move element into view when it is focused
            ref.current.focus();
        }
    }, [focus]);

    return (
        <Dropdown.Item
            data-test-id={createDataTestId({feature: AutomationTestIdFeature.HERO, element: AutomationTestIdElementType.COLLECTION_ITEM, specifier: 'searchResult'})}
            onSelect={onItemClick}
            tabIndex={focus ? 0 : -1}
            ref={ref}
            className="text-truncate rounded-0 py-3"
            as={itemAs}
            eventKey={title}
            title={title}
            href={href}>
            {children}
        </Dropdown.Item>
    );
}

export interface IResultsListProps extends IStyledProps, IEditableProps {
    className?: string;
    itemAs?: string;
    descriptionClassName?: string;
    showEmpty?: boolean;
}

function SearchResults(props: IResultsListProps) {
    const { className, itemAs = "a", descriptionClassName, showEmpty = true } = props;
    const { results, showResults, setStatus } = useSearchContext();
    const [focus, setFocus] = useRoveFocus(results?.length);
    const [preRendered, setPreRendered] = useState(false);
    const { lang } = useLanguage();
    const hasResults = results && results.length > 0;
    // console.error(`search results ${JSON.stringify(results)}`);

    useEffect(() => {
        if (!preRendered && showResults) {
            // The first time the dropdown opens the markup doesn't exist so the animation doesn't work
            // Setting this preRendered flag to true will hold the animation just after the markup is generated
            setPreRendered(true);
        }
    }, [showResults, preRendered]);

    function onSelected (option: string) {
        setStatus({hasError: false, selected: option, isLoading: false});
    }

    return (
        <ResultsWrapper className={classNames(props.editorClassObj, className, 'd-flex w-100')}>
            <div className={classNames("search-results-container w-100", {show: showResults && preRendered && (showEmpty || hasResults)})}>
                <Dropdown show>
                    <Dropdown.Menu className={classNames("rounded-4 p-4 autocomplete search-results-list bg-white border-white")}>
                        {hasResults ? results.map((item, index) =>
                            <ResultItem
                                key={item.key}
                                itemAs={itemAs}
                                setFocus={setFocus}
                                index={index}
                                focus={focus === index}
                                title={item.title}
                                href={item.path}
                                onItemClick={onSelected}
                            >
                                <strong className="results-title">{item.title}</strong>
                                {item.description &&
                                    <span className={classNames(descriptionClassName, "results-description")}>
                                        {item.description}
                                    </span>}
                            </ResultItem>
                        ) : <Dropdown.Item
                            data-test-id={createDataTestId({feature: AutomationTestIdFeature.HERO, element: AutomationTestIdElementType.COLLECTION_ITEM, specifier: "noResults"})}
                            className="text-truncate rounded-0" as="a">
                                <strong className="results-title">{getTranslation(lang).noResults}</strong>
                            </Dropdown.Item>}
                    </Dropdown.Menu>
                </Dropdown>
            </div>
        </ResultsWrapper>
    );
}

export default SearchResults;